import { useCallback } from "react";
import { View } from "react-native";

import type { RemoteOutboundMessage } from "$modules/expo-tv-connect";
import { PlatformKeyCode } from "$modules/expo-tv-connect";
import Button from "$src/core/components/atoms/Button/Button";
import useTranslation from "$src/core/hooks/use-translation";

import $ from "./TvConnectRemoteDpad.styles";

type DpadDirection = "up" | "down" | "left" | "right" | "select";

type Props = {
  onSend: (message: RemoteOutboundMessage) => void;
  showDpad?: boolean;
};

const TvConnectRemoteDpad = ({ onSend, showDpad = true }: Props) => {
  const { t } = useTranslation();

  const sendKey = useCallback(
    (key: PlatformKeyCode) => {
      onSend({
        type: "buttonPress",
        payload: { key },
      });
    },
    [onSend],
  );

  const sendBack = useCallback(() => {
    onSend({
      type: "buttonPress",
      payload: {
        key: PlatformKeyCode.Back,
      },
    });
  }, [onSend]);

  const sendRoute = useCallback(
    (routeKey: "storefront" | "search") => {
      onSend({
        type: "route",
        payload: {
          routeKey,
        },
      });
    },
    [onSend],
  );

  const directionLabel = (direction: DpadDirection) =>
    t("TV_REMOTE/NAVIGATE", { direction });

  return (
    <View style={$.container}>
      <View style={$.pad}>
        {showDpad && (
          <>
            <View style={$.row}>
              <View style={$.slot} />
              <View style={$.slot}>
                <Button
                  name="tv_remote_up"
                  preset="transparent"
                  onPress={() => sendKey(PlatformKeyCode.Up)}
                  accessibilityLabel={directionLabel("up")}
                  icon="arrow-up"
                  isRounded
                />
              </View>
              <View style={$.slot} />
            </View>

            <View style={$.row}>
              <Button
                name="tv_remote_left"
                preset="transparent"
                onPress={() => sendKey(PlatformKeyCode.Left)}
                accessibilityLabel={directionLabel("left")}
                icon="chevron-left"
                isRounded
              />

              <Button
                name="tv_remote_enter"
                preset="transparent"
                onPress={() => sendKey(PlatformKeyCode.Enter)}
                accessibilityLabel={t("TV_REMOTE/OK")}
                label={t("TV_REMOTE/OK")}
                isRounded
              />

              <Button
                name="tv_remote_right"
                preset="transparent"
                onPress={() => sendKey(PlatformKeyCode.Right)}
                accessibilityLabel={directionLabel("right")}
                icon="chevron-right"
                isRounded
              />
            </View>

            <View style={$.row}>
              <View style={$.slot} />
              <View style={$.slot}>
                <Button
                  name="tv_remote_down"
                  preset="transparent"
                  onPress={() => sendKey(PlatformKeyCode.Down)}
                  accessibilityLabel={directionLabel("down")}
                  icon="chevron-down"
                  isRounded
                />
              </View>
              <View style={$.slot} />
            </View>
          </>
        )}

        <View style={$.systemRow}>
          <Button
            name="tv_remote_back"
            preset="transparent"
            onPress={sendBack}
            accessibilityLabel={t("TV_REMOTE/BACK")}
            icon="arrow-turn-down-left"
            label={t("TV_REMOTE/BACK")}
            isRounded
          />

          <Button
            name="tv_remote_home"
            preset="transparent"
            onPress={() => sendRoute("storefront")}
            accessibilityLabel={t("TV_REMOTE/ROUTE/HOME")}
            icon="home"
            label={t("TV_REMOTE/ROUTE/HOME")}
            isRounded
          />

          <Button
            name="tv_remote_search"
            preset="transparent"
            onPress={() => sendRoute("search")}
            accessibilityLabel={t("TV_REMOTE/ROUTE/SEARCH")}
            icon="search"
            label={t("TV_REMOTE/ROUTE/SEARCH")}
            isRounded
          />
        </View>
      </View>
    </View>
  );
};

export default TvConnectRemoteDpad;
